import React from 'react';
import { CARE_PLAN_SUMMARY_STATUS_BADGE } from '~clients/config/carePlanEnums';

import { formatDateString } from '~libs/dayjs';

import LookupStatusBadge from '~components/LookupStatusBadge';
import { Column } from '~components/table-view';
import { linkCellRender } from '~components/table-view/columnRenderers';

import { CarePlanSummaryStatus } from '~clients/models';

/**
 * Column definitions for the Care Plans client-list table.
 * @param onClientClick — navigates to the per-client care plan view for the clicked row.
 *
 * TODO: add a SAH "Classification" column once the /clients/care-plans payload carries it
 * (not on the client model today; needs a product decision on what to surface).
 */
const columnsFactory = (onClientClick: (clientId: number) => void): Column[] => [
  {
    id: 'full_name',
    header: 'Client Name',
    type: 'text',
    // Fallback keeps the cell clickable when a client has no name — linkCellRender skips falsy values.
    deriveValue: (row) => row.full_name || `Client #${row.id}`,
    renderReadOnly: linkCellRender((row) => onClientClick(row.id)),
  },
  {
    id: 'active_plan_count',
    header: 'Active Plans',
    type: 'text',
    deriveValue: (row) => row.active_plan_count ?? 0,
  },
  {
    id: 'last_review_date',
    header: 'Last Reviewed',
    type: 'text',
    deriveValue: (row) => (row.last_review_date ? formatDateString(row.last_review_date) : '—'),
  },
  {
    id: 'next_review_date',
    header: 'Next Review Due',
    type: 'text',
    // Overdue rows surface the next review date in red (per ticket scope).
    renderReadOnly: (_value, row) => {
      const text = row.next_review_date ? formatDateString(row.next_review_date) : '—';
      return row.care_plan_status === 'overdue' ? (
        <span className="text-danger fw-semibold">{text}</span>
      ) : (
        <span>{text}</span>
      );
    },
  },
  {
    id: 'care_plan_status',
    header: 'Status',
    type: 'custom',
    classNames: {
      header: 'cell-align-center',
      column: 'cell-align-center',
    },

    // Reuse the shared LookupStatusBadge — the app-wide single source of truth for status badges.
    component: LookupStatusBadge,
    componentProps: (row) => ({
      // Fall back to the "None active" badge for any unmapped/missing status. LookupStatusBadge
      // destructures `status` with no null guard of its own, so an unexpected value from
      // GET /clients/care-plans would otherwise crash the whole row.
      status:
        CARE_PLAN_SUMMARY_STATUS_BADGE[row.care_plan_status as CarePlanSummaryStatus] ??
        CARE_PLAN_SUMMARY_STATUS_BADGE.none,
      dataCy: `care-plan-status-${row.id}`,
    }),
  },
];

export default columnsFactory;
